import React from 'react'
import { cn } from '@/utils'
import { ArrowUpDown, ArrowUpRight, Layers, TrendingDown, TrendingUp, Wallet2 } from 'lucide-react'

export interface PortfolioMetricCardProps {
  icon: 'invested' | 'current' | 'gain' | 'assets'
  label: string
  value: string
  subValue?: string
  trend?: 'up' | 'down'
  className?: string
  chart?: React.ReactNode
}

const iconMap = {
  invested: Wallet2,
  current: ArrowUpRight,
  gain: ArrowUpDown,
  assets: Layers,
}

export const PortfolioMetricCard: React.FC<PortfolioMetricCardProps> = ({
  icon,
  label,
  value,
  subValue,
  trend,
  className,
  chart,
}) => {
  const Icon = iconMap[icon]
  const isCurrency = value.startsWith('$')
  const valueWithoutCurrency = isCurrency ? value.slice(1).trimStart() : value

  return (
    <div
      className={cn(
        'relative h-[124px] overflow-hidden border border-[rgba(45,41,35,0.08)] bg-white dark:border-[rgba(255,255,255,0.06)] dark:bg-[#1c1c1c]',
        className
      )}
    >
      <div className="absolute left-[24px] top-[24px] z-10 flex h-[76px] flex-col items-start justify-between">
        <div className="flex w-full items-center gap-[6px]">
          <div className="size-[16px] shrink-0 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            <Icon className="w-full h-full" strokeWidth={1.5} />
          </div>
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            {label}
          </p>
        </div>

        <div className="flex items-center gap-[4px]">
          <p className="font-['Plus_Jakarta_Sans'] text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            {isCurrency ? (
              <>
                <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
                <span>{` ${valueWithoutCurrency}`}</span>
              </>
            ) : (
              value
            )}
          </p>
          {subValue && (
            <div className="relative flex items-center justify-center gap-[4px] rounded-[16px] bg-[rgba(242,241,246,0.64)] pb-[4px] pl-[6px] pr-[8px] pt-[3px] dark:bg-[rgba(13,13,13,0.64)]">
              {trend === 'up' ? (
                <TrendingUp size={12} className="text-[#2ea66b]" />
              ) : (
                <TrendingDown size={12} className="text-[#ff540c]" />
              )}
              <p className={`font-['Plus_Jakarta_Sans'] text-[12px] font-semibold leading-[16px] ${
                trend === 'up' ? 'text-[#2ea66b]' : 'text-[#ff540c]'
              }`}>
                {subValue}
              </p>
              <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_1px_1px_4px_0px_rgba(217,214,227,0.4)] dark:shadow-[inset_1px_1px_4px_0px_rgba(0,0,0,0.4)]" />
            </div>
          )}
        </div>
      </div>

      {chart && (
        <div className="absolute right-0 top-0 h-[124px] w-[185px] overflow-hidden">
          {chart}
        </div>
      )}
    </div>
  )
}

export default PortfolioMetricCard
